import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";

type CompareVersionMenuProps = {
	asset: any;

	primaryVersionId: string;
	compareVersionId: string;
	compareEnabled: boolean;

	onClick?: () => void;
	onSelectVersion: (versionId: string) => void;
	onClearCompare: () => void;
};

function getVersionId(versionEntry: any) {
	return versionEntry?.ref?.id || versionEntry?.id || "";
}

function normalizeText(value: any) {
	return String(value || "").trim();
}

function getFirstText(...values: any[]) {
	for (const value of values) {
		const text = normalizeText(value);

		if (text.length) {
			return text;
		}
	}

	return "";
}

function getVersionComment(versionEntry: any) {
	return getFirstText(
		versionEntry?.commit_message,
		versionEntry?.commitMessage,
		versionEntry?.comment,
		versionEntry?.comments,
		versionEntry?.description,
		versionEntry?.note,
		versionEntry?.notes,
		versionEntry?.values?.commit_message,
		versionEntry?.values?.commitMessage,
		versionEntry?.values?.comment,
		versionEntry?.values?.comments,
		versionEntry?.values?.description,
		versionEntry?.values?.note,
		versionEntry?.values?.notes
	);
}

function formatVersionNameCandidate(value: any) {
	const text = normalizeText(value);

	if (!text.length) {
		return "";
	}

	if (/^v\d+$/i.test(text)) {
		return text.toLowerCase();
	}

	if (/^\d+$/.test(text)) {
		return `v${text}`;
	}

	return text;
}

function getVersionName(versionEntry: any, fallbackIndexFromNewest: number) {
	const directName = getFirstText(
		versionEntry?.name,
		versionEntry?.label,
		versionEntry?.version_name,
		versionEntry?.versionName,
		versionEntry?.display_name,
		versionEntry?.displayName,
		versionEntry?.values?.name,
		versionEntry?.values?.label,
		versionEntry?.values?.version_name,
		versionEntry?.values?.versionName,
		versionEntry?.values?.display_name,
		versionEntry?.values?.displayName
	);

	const formattedDirectName = formatVersionNameCandidate(directName);

	if (formattedDirectName.length) {
		return formattedDirectName;
	}

	const versionNumber = getFirstText(
		versionEntry?.asset_version_number,
		versionEntry?.version,
		versionEntry?.version_number,
		versionEntry?.versionNumber,
		versionEntry?.number,
		versionEntry?.values?.asset_version_number,
		versionEntry?.values?.version,
		versionEntry?.values?.version_number,
		versionEntry?.values?.versionNumber,
		versionEntry?.values?.number
	);

	const formattedVersionNumber = formatVersionNameCandidate(versionNumber);

	if (formattedVersionNumber.length) {
		return formattedVersionNumber;
	}

	const fallbackVersionNumber = fallbackIndexFromNewest + 1;

	return `v${fallbackVersionNumber}`;
}

function getVersionRoleLabel(isPrimary: boolean, isCompare: boolean) {
	if (isPrimary && isCompare) {
		return "A & B";
	}

	if (isPrimary) {
		return "A";
	}

	if (isCompare) {
		return "B";
	}

	return "";
}

function getSortedVersions(asset: any) {
	return [...(asset?.values?.versions || [])].filter((versionEntry: any) => Boolean(getVersionId(versionEntry))).sort((a: any, b: any) => getVersionId(b).localeCompare(getVersionId(a)));
}

function CompareVersionMenuComponent({ asset, primaryVersionId, compareVersionId, compareEnabled, onClick, onSelectVersion, onClearCompare }: CompareVersionMenuProps) {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const open = Boolean(anchorEl);

	const versions = React.useMemo(() => {
		return getSortedVersions(asset);
	}, [asset]);

	const selectedVersionLabel = React.useMemo(() => {
		if (!compareEnabled || !compareVersionId) {
			return "Compare";
		}

		const selectedVersionIndex = versions.findIndex((versionEntry: any) => getVersionId(versionEntry) === compareVersionId);
		const selectedVersion = selectedVersionIndex > -1 ? versions[selectedVersionIndex] : null;

		return selectedVersion ? getVersionName(selectedVersion, selectedVersionIndex) : compareVersionId;
	}, [compareEnabled, compareVersionId, versions]);

	const selectedVersionComment = React.useMemo(() => {
		if (!compareEnabled || !compareVersionId) {
			return "";
		}

		const selectedVersionIndex = versions.findIndex((versionEntry: any) => getVersionId(versionEntry) === compareVersionId);
		const selectedVersion = selectedVersionIndex > -1 ? versions[selectedVersionIndex] : null;

		return selectedVersion ? getVersionComment(selectedVersion) : "";
	}, [compareEnabled, compareVersionId, versions]);

	const handleOpen = React.useCallback(
		(evt: React.MouseEvent<HTMLButtonElement>) => {
			evt.preventDefault();
			evt.stopPropagation();

			onClick?.();
			setAnchorEl(evt.currentTarget);
		},
		[onClick]
	);

	const handleClose = React.useCallback((evt?: any) => {
		evt?.preventDefault?.();
		evt?.stopPropagation?.();

		setAnchorEl(null);
	}, []);

	const handleSelectVersion = React.useCallback(
		(evt: any, nextVersionId: string, disabled: boolean) => {
			evt.preventDefault();
			evt.stopPropagation();

			if (disabled) {
				return;
			}

			onSelectVersion(nextVersionId);
			setAnchorEl(null);
		},
		[onSelectVersion]
	);

	const handleClear = React.useCallback(
		(evt: any) => {
			evt.preventDefault();
			evt.stopPropagation();

			onClearCompare();
			setAnchorEl(null);
		},
		[onClearCompare]
	);

	return (
		<>
			<Button
				color={compareEnabled ? "secondary" : "inherit"}
				size="small"
				variant={compareEnabled ? "outlined" : "text"}
				disabled={!versions.length}
				onClick={handleOpen}
				endIcon={<ArrowDropDownIcon fontSize="inherit" />}
				sx={{
					ml: 1,
					height: "32px",
					minHeight: "32px",
					maxWidth: 220,
					px: 1,
					borderRadius: 1,
					color: compareEnabled ? "secondary.main" : "text.primary",
					borderColor: compareEnabled ? "secondary.main" : "transparent",
					textTransform: "none",
					fontWeight: 700,
					fontSize: "0.875rem",
					lineHeight: 1.75,
					whiteSpace: "nowrap",
					overflow: "hidden",
					"&:hover": {
						borderColor: compareEnabled ? "secondary.main" : "transparent",
						backgroundColor: compareEnabled ? "rgba(16, 185, 129, 0.08)" : "rgba(255,255,255,0.04)",
					},
					"& .MuiButton-endIcon": {
						ml: 0.25,
					},
				}}
			>
				<Box
					component="span"
					sx={{
						display: "block",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontWeight: 700,
					}}
				>
					{selectedVersionLabel}
				</Box>
			</Button>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				onClick={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				MenuListProps={{
					"aria-labelledby": "compare-version-menu-button",
				}}
				PaperProps={{
					style: {
						maxHeight: "33%",
						left: "50%",
						transform: "translateY(10px)",
					},
				}}
			>
				{/* <MenuItem disabled dense>
					<ListItemText
						primary="Compare"
						secondary={compareEnabled ? (selectedVersionComment ? `Selected: ${selectedVersionLabel} · ${selectedVersionComment}` : `Selected: ${selectedVersionLabel}`) : "Select a version to compare"}
						primaryTypographyProps={{
							noWrap: true,
							fontWeight: 700,
							fontSize: "0.875rem",
							lineHeight: 1.75,
						}}
						secondaryTypographyProps={{
							noWrap: true,
							fontSize: "0.75rem",
							lineHeight: 1.4,
							color: "text.secondary",
						}}
						sx={{
							my: 0,
							minWidth: 0,
							maxWidth: 300,
						}}
					/>
				</MenuItem> */}

				{compareEnabled ? (
					<MenuItem dense onClick={handleClear}>
						<CloseIcon fontSize="small" style={{ marginRight: 8 }} />
						<ListItemText
							primary="Disable comparison"
							primaryTypographyProps={{
								noWrap: true,
								fontWeight: 700,
								fontSize: "0.875rem",
								lineHeight: 1.75,
							}}
							sx={{
								my: 0,
								minWidth: 0,
							}}
						/>
					</MenuItem>
				) : null}

				{compareEnabled ? <Divider /> : null}

				{versions.map((versionEntry: any, index: number) => {
					const nextVersionId = getVersionId(versionEntry);
					const isPrimary = nextVersionId === primaryVersionId;
					const isCompare = compareEnabled && nextVersionId === compareVersionId;
					const isDisabled = isPrimary;
					const comment = getVersionComment(versionEntry);
					const versionName = getVersionName(versionEntry, index);
					const roleLabel = getVersionRoleLabel(isPrimary, isCompare);

					return (
						<MenuItem
							key={nextVersionId}
							dense
							disabled={isDisabled}
							selected={isCompare}
							onClick={(evt) => handleSelectVersion(evt, nextVersionId, isDisabled)}
							sx={{
								backgroundColor: isCompare ? "rgba(16, 185, 129, 0.12)" : undefined,
								opacity: isDisabled ? "1!important" : undefined,
								cursor: isDisabled ? "not-allowed" : "pointer",
								"&.Mui-disabled": {
									opacity: "1!important",
									color: "text.primary",
								},
								"&.Mui-selected": {
									backgroundColor: "rgba(16, 185, 129, 0.16)",
								},
								"&.Mui-selected:hover": {
									backgroundColor: "rgba(16, 185, 129, 0.22)",
								},
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									minWidth: 0,
									width: "100%",
									opacity: isDisabled ? 0.65 : 1,
								}}
							>
								<Box
									sx={{
										width: 8,
										height: 8,
										borderRadius: "50%",
										mr: 1,
										flex: "0 0 auto",
										backgroundColor: isPrimary ? "primary.main" : isCompare ? "secondary.main" : "transparent",
									}}
								/>

								<ListItemText
									primary={
										<Box
											component="span"
											sx={{
												display: "flex",
												alignItems: "baseline",
												gap: 0.75,
												minWidth: 0,
											}}
										>
											<Box
												component="span"
												sx={{
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													fontWeight: 700,
												}}
											>
												{versionName}
											</Box>

											{roleLabel ? (
												<Box
													component="span"
													sx={{
														flex: "0 0 auto",
														opacity: 0.75,
														fontSize: "0.75rem",
														fontWeight: 700,
													}}
												>
													({roleLabel})
												</Box>
											) : null}
										</Box>
									}
									secondary={comment || undefined}
									primaryTypographyProps={{
										noWrap: true,
										component: "div",
										fontWeight: 700,
										fontSize: "0.875rem",
										lineHeight: 1.75,
									}}
									secondaryTypographyProps={{
										noWrap: true,
										title: comment,
										fontSize: "0.75rem",
										lineHeight: 1.4,
										color: "text.secondary",
									}}
									sx={{
										my: 0,
										minWidth: 0,
										maxWidth: 300,
									}}
								/>
							</Box>
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
}

export const CompareVersionMenu = React.memo(CompareVersionMenuComponent);
